import { CommonModule } from '@angular/common';
import {
  Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges
} from '@angular/core';
import { RouterModule } from '@angular/router';

import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  Subject,
  Observable
} from 'rxjs';
import {
  map,
  takeUntil,
  switchMap,
  distinctUntilChanged
} from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import {
  EmptyURIValue,
  encodeSpecialCharacters,
  URIPrefix,
  STATIC_STATE_INSURANCE_LISTS
} from '@sondermind/data-access/directory-link';
import {
  BrowseLinkKeys, BrowseLinkKeyType, IAPIListResponse, IBrowseLinkApiResponse, IBrowseLinkData
} from '@sondermind/utilities/models-http';

import { BrowseLinksStore } from '@sondermind/data-access/public-facing/browse-links';
import { USStateAbbrevMap } from '@sondermind/utilities/states-list';

@Component({
  imports: [
    CommonModule,
    RouterModule,
  ],
  selector: 'browse-therapists-card',
  templateUrl: './browse-therapists-card.component.html',
  styleUrls: ['./browse-therapists-card.component.scss']
})
export class BrowseTherapistsCard implements OnInit, OnChanges, OnDestroy {
  @Input() currentStateAbbrev!: string;
  @Input() selectedCity?: string;

  private loadDataTrigger$ = new Subject<void>();
  private destroyed$ = new Subject<void>();

  private sortedCities: IBrowseLinkData[] = [];
  private sortedInsurances: IBrowseLinkData[] = [];
  private sortedSpecialities: IBrowseLinkData[] = [];
  private sortedTreatmentPreferences: IBrowseLinkData[] = [];
  private readonly defaultItemCount = 5;
  readonly emptyURIValue = EmptyURIValue;
  readonly uriPrefix = URIPrefix;
  providerDirectoryUrl: string = '';
  currentStateName: string = '';

  uriSafeCity$ = new BehaviorSubject<string>('');
  cities$ = new BehaviorSubject<IBrowseLinkData[]>([]);
  insurances$ = new BehaviorSubject<IBrowseLinkData[]>([]);
  specialities$ = new BehaviorSubject<IBrowseLinkData[]>([]);
  treatmentPreferences$ = new BehaviorSubject<IBrowseLinkData[]>([]);
  showAll$ = new BehaviorSubject<boolean>(false);
  hasMore$ = new BehaviorSubject<boolean>(false);

  combinedBrowseLinks$ = combineLatest([
    this.specialities$,
    this.insurances$,
    this.cities$,
    this.treatmentPreferences$
  ]).pipe(
    map(([specialities, insurances, cities, treatmentPreferences]) => ({
      specialities,
      insurances,
      cities,
      treatmentPreferences
    }))
  );

  constructor(
    private browseLinksStore: BrowseLinksStore,
    private configurationService: ConfigurationService
  ) {}

  ngOnInit(): void {
    this.providerDirectoryUrl =
    this.configurationService.env.providerDirectoryBase ||
    'https://www.sondermind.com/provider-directory';

    this.initializeDataLoader();
    this.initializeShowAllSubscription();

    if (this.currentStateAbbrev?.trim()) this.loadDataTrigger$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const stateChanged = changes['currentStateAbbrev']?.currentValue as string | undefined;
    const cityChanged = changes['selectedCity']?.currentValue as string | undefined;

    if (stateChanged) {
      this.currentStateAbbrev = stateChanged;
      this.updateHeaderText(stateChanged);
    }

    if (cityChanged) {
      this.selectedCity = cityChanged;
      this.updateUriSafeCity(cityChanged);
    }

    if (this.currentStateAbbrev?.trim()) {
      this.loadDataTrigger$.next();
    }
  }

  private updateUriSafeCity(city: string | undefined): void {
    const isValidCity = city && city !== this.emptyURIValue;
    this.uriSafeCity$.next(
      isValidCity ? encodeURIComponent(encodeSpecialCharacters(city.toLocaleLowerCase())) : ''
    );
  }

  private updateHeaderText(stateAbbrev?: string): void {
    const cleanAbbrev = stateAbbrev?.trim();
    this.currentStateName = cleanAbbrev ? (USStateAbbrevMap[cleanAbbrev] || cleanAbbrev) : '';
  }

  private initializeShowAllSubscription(): void {
    this.showAll$.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.processStoredData();
    });
  }

  private initializeDataLoader(): void {
    this.loadDataTrigger$
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(() => this.getBrowseDataObservable())
      ).subscribe(({ stateData, cityData }) => {
        const dataSource = cityData?.data ?? stateData.data;

        this.sortedCities = this.getTopItems(stateData.data, BrowseLinkKeys.CITY);
        this.sortedInsurances = this.getTopItems(dataSource, BrowseLinkKeys.INSURANCE);
        this.sortedSpecialities = this.getTopItems(dataSource, BrowseLinkKeys.SPECIALTY);
        this.sortedTreatmentPreferences = this.getTopItems(dataSource, BrowseLinkKeys.TREATMENT_PREFERENCE);
        // Calculate hasMore$ once when data is loaded
        const hasMoreCities = this.sortedCities.length > this.defaultItemCount;
        const hasMoreInsurances = this.sortedInsurances.length > this.defaultItemCount;
        const hasMoreSpecialities = this.sortedSpecialities.length > this.defaultItemCount;
        const hasMoreTreatmentPreferences = this.sortedTreatmentPreferences.length > this.defaultItemCount;
        this.hasMore$.next(hasMoreCities || hasMoreInsurances || hasMoreSpecialities || hasMoreTreatmentPreferences);

        this.processStoredData();
      });
  }

  private processStoredData(): void {
    const shouldShowAll = this.showAll$.getValue();
    const itemCount = shouldShowAll ? undefined : this.defaultItemCount;

    this.cities$.next(
      itemCount ? this.sortedCities.slice(0, itemCount) : this.sortedCities
    );

    this.insurances$.next(
      itemCount ? this.sortedInsurances.slice(0, itemCount) : this.sortedInsurances
    );

    this.specialities$.next(
      itemCount ? this.sortedSpecialities.slice(0, itemCount) : this.sortedSpecialities
    );

    this.treatmentPreferences$.next(
      itemCount ? this.sortedTreatmentPreferences.slice(0, itemCount) : this.sortedTreatmentPreferences
    );
  }

  private getBrowseDataObservable(): Observable<{
    stateData: IAPIListResponse<IBrowseLinkApiResponse>;
    cityData: IAPIListResponse<IBrowseLinkApiResponse> | null;
  }> {
    const stateLinks$ = this.browseLinksStore.getBrowseLinks$(this.currentStateAbbrev);

    if (this.uriSafeCity$.getValue() && this.selectedCity) {
      const cityLinks$ = this.browseLinksStore.getBrowseCityLinks$(this.currentStateAbbrev, this.selectedCity);

      return forkJoin({ stateData: stateLinks$, cityData: cityLinks$ });
    }

    return stateLinks$.pipe(map((stateData) => ({ stateData, cityData: null })));
  }

  private getTopItems(
    items: IBrowseLinkApiResponse[],
    key: BrowseLinkKeyType
  ): IBrowseLinkData[] {
    if (!items?.length) {
      return [];
    }

    // Special handling for insurance
    if (key === BrowseLinkKeys.INSURANCE) {
      const stateAbbrev = this.currentStateAbbrev.toLowerCase();
      const stateInsurances = STATIC_STATE_INSURANCE_LISTS[stateAbbrev];

      if (stateInsurances) {
        return this.filterAndSortInsuranceItems(items, stateInsurances);
      }
    }

    return this.filterAndSortItems(items, key);
  }

  private filterAndSortInsuranceItems(
    items: IBrowseLinkApiResponse[],
    stateInsurances: string[]
  ): IBrowseLinkData[] {
    const validInsurances = new Set(stateInsurances.map((ins) => ins.toLowerCase()));

    // Split items into valid and remaining insurances
    const { validItems, remainingItems } = items.reduce<{
      validItems: IBrowseLinkApiResponse[];
      remainingItems: IBrowseLinkApiResponse[];
    }>((acc, item) => {
      const insurance = item.insurance?.toLowerCase();
      if (!insurance) return acc;

      if (validInsurances.has(insurance)) {
        acc.validItems.push(item);
      } else {
        acc.remainingItems.push(item);
      }
      return acc;
    }, { validItems: [], remainingItems: [] });

    // Sort valid insurances by provider count
    const sortedValidItems = validItems.sort((a, b) => b.provider_count - a.provider_count);

    // Get remaining insurances sorted
    const remainingSortedItems = this.filterAndSortItems(
      remainingItems,
      BrowseLinkKeys.INSURANCE
    );

    // Combine and return results
    return [
      ...this.mapToBrowseLinkData(sortedValidItems, BrowseLinkKeys.INSURANCE),
      ...remainingSortedItems
    ];
  }

  private filterAndSortItems(
    items: IBrowseLinkApiResponse[],
    key: BrowseLinkKeyType
  ): IBrowseLinkData[] {
    const keyField = key as keyof IBrowseLinkApiResponse;

    const sortedItems = items
      .filter((item) => item[keyField] != null)
      .sort((a, b) => {
        // First sort by provider_count in descending order
        const countDiff = b.provider_count - a.provider_count;
        if (countDiff !== 0) return countDiff;

        // If provider_count is equal, sort alphabetically by the key value
        const aValue = (a[keyField] ?? '').toString().toLowerCase();
        const bValue = (b[keyField] ?? '').toString().toLowerCase();
        return aValue.localeCompare(bValue);
      });

    return this.mapToBrowseLinkData(sortedItems, key);
  }

  private mapToBrowseLinkData(
    items: IBrowseLinkApiResponse[],
    key: BrowseLinkKeyType
  ): IBrowseLinkData[] {
    const keyTyped = key as keyof IBrowseLinkApiResponse;

    return items.map((item) => {
      const value = item[keyTyped] as string;
      return {
        value,
        uriSafeValue: encodeURIComponent(encodeSpecialCharacters(value.toLocaleLowerCase()))
      };
    });
  }

  toggleShowAll(): void {
    this.showAll$.next(!this.showAll$.getValue());
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
